import { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

interface ScoreMetric {
  label: string;
  value: number;
  suffix: string;
  prefix?: string;
  description: string;
  icon: string;
  change: string;
  changeDirection: 'up' | 'down';
}

interface CategoryData {
  id: string;
  label: string;
  metrics: ScoreMetric[];
}

const categories: CategoryData[] = [
  {
    id: 'efficiency',
    label: 'Delivery',
    metrics: [
      {
        label: 'Time to First Engineer',
        value: 14,
        suffix: ' days',
        description: 'From inquiry to onboarded engineer',
        icon: 'ri-time-line',
        change: '-3 days',
        changeDirection: 'down',
      },
      {
        label: 'On-Time Delivery',
        value: 97,
        suffix: '%',
        description: 'Projects on or ahead of schedule',
        icon: 'ri-calendar-check-line',
        change: '+4%',
        changeDirection: 'up',
      },
      {
        label: 'Avg. Duration',
        value: 4.2,
        suffix: ' mo',
        description: 'Typical full-team engagement',
        icon: 'ri-hourglass-line',
        change: '-0.8 mo',
        changeDirection: 'down',
      },
    ],
  },
  {
    id: 'cost',
    label: 'Cost',
    metrics: [
      {
        label: 'Client Cost Saved',
        value: 12.4,
        prefix: '$',
        suffix: 'M',
        description: 'Cumulative vs traditional hiring',
        icon: 'ri-money-dollar-circle-line',
        change: '+$2.1M',
        changeDirection: 'up',
      },
      {
        label: 'Cost Reduction',
        value: 32,
        suffix: '%',
        description: 'Vs in-house hiring + overhead',
        icon: 'ri-arrow-down-circle-line',
        change: '+5%',
        changeDirection: 'up',
      },
      {
        label: 'Retention Saved',
        value: 1.8,
        prefix: '$',
        suffix: 'M',
        description: 'Avoided turnover costs',
        icon: 'ri-exchange-dollar-line',
        change: '+$340K',
        changeDirection: 'up',
      },
    ],
  },
  {
    id: 'quality',
    label: 'Quality',
    metrics: [
      {
        label: 'Satisfaction',
        value: 4.8,
        suffix: '/5.0',
        description: 'Avg. across all engagements',
        icon: 'ri-star-line',
        change: '+0.2',
        changeDirection: 'up',
      },
      {
        label: 'Retention Rate',
        value: 94,
        suffix: '%',
        description: 'Active after 6 months',
        icon: 'ri-user-heart-line',
        change: '+6%',
        changeDirection: 'up',
      },
      {
        label: 'NPS Score',
        value: 72,
        suffix: '',
        description: 'Net Promoter from surveys',
        icon: 'ri-emotion-happy-line',
        change: '+8',
        changeDirection: 'up',
      },
    ],
  },
  {
    id: 'scale',
    label: 'Scale',
    metrics: [
      {
        label: 'Engineers Deployed',
        value: 200,
        suffix: '+',
        description: 'Total placed to date',
        icon: 'ri-user-follow-line',
        change: '+42',
        changeDirection: 'up',
      },
      {
        label: 'Active Teams',
        value: 50,
        suffix: '+',
        description: 'Ongoing engagements now',
        icon: 'ri-building-line',
        change: '+12',
        changeDirection: 'up',
      },
      {
        label: 'Countries',
        value: 18,
        suffix: '',
        description: 'Global client footprint',
        icon: 'ri-global-line',
        change: '+3',
        changeDirection: 'up',
      },
    ],
  },
];

const benchmarks = [
  { label: 'Time to Hire', us: 14, industry: 48, unit: 'days', better: 'lower' },
  { label: 'Cost per Hire', us: 4200, industry: 8900, unit: 'USD', better: 'lower' },
  { label: '6-Month Retention', us: 94, industry: 72, unit: '%', better: 'higher' },
  { label: 'First-Week Productivity', us: 78, industry: 45, unit: '%', better: 'higher' },
];

const exploreCards = [
  { title: 'Find Talent', to: '/talent', icon: 'ri-user-search-line', color: 'bg-rose-50 text-rose-600 border-rose-200' },
  { title: 'How It Works', to: '/how-it-works', icon: 'ri-route-line', color: 'bg-violet-50 text-violet-600 border-violet-200' },
  { title: 'Case Studies', to: '/case-studies', icon: 'ri-folder-chart-line', color: 'bg-orange-50 text-orange-600 border-orange-200' },
  { title: 'Trust & Security', to: '/trust-security', icon: 'ri-shield-check-line', color: 'bg-teal-50 text-teal-600 border-teal-200' },
];

function AnimatedCounter({ target, suffix, prefix, inView }: { target: number; suffix: string; prefix?: string; inView: boolean }) {
  const [count, setCount] = useState(0);
  const countRef = useRef(0);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (!inView) return;
    countRef.current = 0;
    const duration = 1500;
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const val = eased * target;
      countRef.current = target % 1 !== 0 ? parseFloat(val.toFixed(1)) : Math.round(val);
      setCount(countRef.current);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [inView, target]);

  return (
    <span>{prefix}{count}{suffix}</span>
  );
}

export default function ClientSuccessScorecard() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 });
  const [activeCategory, setActiveCategory] = useState('efficiency');

  const activeData = categories.find((c) => c.id === activeCategory)!;

  return (
    <section id="scorecard" className="py-10 md:py-14 bg-stone-50/60 relative overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.6 }}
          className="text-center mb-6 md:mb-8"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-primary-200 rounded-full mb-3 shadow-sm"
          >
            <i className="ri-bar-chart-box-line text-primary-600"></i>
            <span className="text-sm font-medium text-gray-700">Aggregate Performance</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6 mb-3"
          >
            Client Success <span className="text-primary-600">Scorecard</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto"
          >
            Every number here represents a real client outcome. Here is how we stack up across every engagement to date.
          </motion.p>
        </motion.div>

        {/* Category Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.25, ...springTransition }}
          className="flex flex-wrap justify-center gap-2 mb-6 md:mb-8"
        >
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeCategory === cat.id
                  ? 'bg-primary-500 text-white shadow-md shadow-primary-500/15'
                  : 'bg-white text-navy-900 border border-gray-200 hover:border-primary-300 hover:bg-primary-50'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </motion.div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-8 md:mb-10">
          {activeData.metrics.map((metric, index) => (
            <motion.div
              key={metric.label}
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ ...springTransition, duration: 0.5, delay: index * 0.08 }}
              className="bg-white rounded-xl border border-gray-100 p-5 md:p-6 hover:border-primary-200 hover:shadow-md hover:shadow-primary-500/5 transition-all duration-500 group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 group-hover:bg-primary-100 transition-colors">
                  <i className={`${metric.icon} text-lg text-primary-600`}></i>
                </div>
                <span
                  className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${
                    metric.changeDirection === 'up'
                      ? 'text-emerald-700 bg-emerald-50'
                      : 'text-primary-700 bg-primary-50'
                  }`}
                >
                  <i className={metric.changeDirection === 'up' ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}></i>
                  {metric.change}
                </span>
              </div>

              <div className="text-3xl md:text-4xl font-bold text-navy-900 mb-1">
                <AnimatedCounter
                  target={metric.value}
                  suffix={metric.suffix}
                  prefix={metric.prefix}
                  inView={isInView && activeCategory === activeData.id}
                />
              </div>

              <h3 className="text-sm md:text-base font-bold text-navy-900 mb-0.5">{metric.label}</h3>
              <p className="text-xs text-gray-500">{metric.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Benchmark Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.6, delay: 0.35 }}
          className="bg-white rounded-xl border border-gray-100 p-5 md:p-6 mb-6 md:mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-5 gap-2">
            <div>
              <h3 className="text-lg md:text-xl font-bold text-navy-900">How We Compare</h3>
              <p className="text-xs text-gray-500">Thoughtic vs. industry averages</p>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-primary-500"></span>
                Thoughtic
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-gray-300"></span>
                Industry
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {benchmarks.map((bench, index) => {
              const isHigherBetter = bench.better === 'higher';
              const ratio = bench.us / bench.industry;
              const barPercent = isHigherBetter
                ? Math.min((bench.us / bench.industry) * 50, 100)
                : Math.min((bench.industry / bench.us) * 50, 100);

              return (
                <motion.div
                  key={bench.label}
                  initial={{ opacity: 0, y: 15 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.45 + index * 0.08, duration: 0.4 }}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-sm font-bold text-navy-900">{bench.label}</p>
                    <span className="text-xs font-semibold text-emerald-700">
                      {isHigherBetter ? `${Math.round((ratio - 1) * 100)}% better` : `${Math.round((1 - ratio) * 100)}% faster`}
                    </span>
                  </div>

                  {/* Us bar */}
                  <div className="mb-1">
                    <div className="flex items-center justify-between text-xs mb-0.5">
                      <span className="font-medium text-primary-600">Thoughtic</span>
                      <span className="font-bold text-navy-900">
                        {bench.unit === 'USD' ? `$${bench.us.toLocaleString()}` : `${bench.us}${bench.unit}`}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={isInView ? { width: `${barPercent}%` } : {}}
                        transition={{ delay: 0.55 + index * 0.08, duration: 0.8, ease: 'easeOut' }}
                        className="h-full bg-primary-500 rounded-full"
                      ></motion.div>
                    </div>
                  </div>

                  {/* Industry bar */}
                  <div>
                    <div className="flex items-center justify-between text-xs mb-0.5">
                      <span className="font-medium text-gray-500">Industry</span>
                      <span className="font-medium text-gray-500">
                        {bench.unit === 'USD' ? `$${bench.industry.toLocaleString()}` : `${bench.industry}${bench.unit}`}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={isInView ? { width: '50%' } : {}}
                        transition={{ delay: 0.6 + index * 0.08, duration: 0.8, ease: 'easeOut' }}
                        className="h-full bg-gray-300 rounded-full"
                      ></motion.div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Bottom row: Trust chips + Explore cards merged */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5, ...springTransition }}
        >
          {/* Trust chips */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            <div className="bg-white rounded-lg border border-gray-100 p-3.5 flex items-center gap-3 hover:border-primary-200 transition-colors">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-50 flex-shrink-0">
                <i className="ri-shield-check-line text-lg text-primary-600"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-navy-900">Zero failed engagements</p>
                <p className="text-xs text-gray-500">Every client renewed or expanded</p>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-100 p-3.5 flex items-center gap-3 hover:border-primary-200 transition-colors">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-emerald-50 flex-shrink-0">
                <i className="ri-thumb-up-line text-lg text-emerald-600"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-navy-900">96% renewal rate</p>
                <p className="text-xs text-gray-500">Clients stay and scale with us</p>
              </div>
            </div>

            <button
              className="bg-white rounded-lg border border-gray-100 p-3.5 flex items-center gap-3 hover:border-primary-200 transition-colors cursor-pointer group text-left"
              onClick={() => {
                const el = document.getElementById('testimonials');
                if (el) {
                  const offset = 80;
                  const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
                  window.scrollTo({ top: pos, behavior: 'smooth' });
                }
              }}
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-50 group-hover:bg-primary-100 transition-colors flex-shrink-0">
                <i className="ri-message-3-line text-lg text-primary-600"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-navy-900 group-hover:text-primary-600 transition-colors truncate">Read testimonials</p>
                <p className="text-xs text-gray-500">What CTOs and VPs say</p>
              </div>
              <i className="ri-arrow-right-line text-gray-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all flex-shrink-0"></i>
            </button>
          </div>

          {/* Explore More — merged into scorecard section */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 md:p-5">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-compass-3-line text-primary-600"></i>
              <span className="text-sm font-bold text-navy-900">Explore More</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {exploreCards.map((card, i) => (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 10 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.55 + i * 0.06, duration: 0.4 }}
                >
                  <Link
                    to={card.to}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-primary-200 hover:shadow-sm hover:-translate-y-0.5 transition-all duration-300 group"
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${card.color}`}>
                      <i className={`${card.icon} text-lg`}></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-navy-900 group-hover:text-primary-600 transition-colors truncate">{card.title}</p>
                    </div>
                    <i className="ri-arrow-right-line text-gray-400 group-hover:text-primary-600 group-hover:translate-x-0.5 transition-all ml-auto flex-shrink-0 text-sm"></i>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}